/**
 * analytics-events.js
 * -------------------
 * Google Analytics wiring for source attribution.
 *
 * Depends on window.TrafficSource (traffic-source.js) — MUST load after it.
 * Works with either gtag.js (window.gtag) or a raw dataLayer (window.dataLayer).
 *
 * Fires:
 *   - traffic_source_detected  (once per session, on landing)
 *   - add_to_cart_source       (delegated form submit + Dawn pub/sub backup)
 *   - reached_checkout_source  (delegated click on checkout affordances)
 *   - amazon_buy_click         (delegated click on [data-amazon-buy])
 *
 * Safe with zero live campaigns: every payload still carries channel:'direct'
 * and null utm values — no empty events, no throws.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Event names + param keys — rename here, not scattered through code.
   * ------------------------------------------------------------------ */
  var EVENTS = {
    landing: 'traffic_source_detected',
    addToCart: 'add_to_cart_source',
    checkout: 'reached_checkout_source',
    amazonClick: 'amazon_buy_click',
  };

  var PARAMS = {
    channel: 'channel',
    source: 'utm_source',
    medium: 'utm_medium',
    campaign: 'utm_campaign',
    content: 'utm_content',
    term: 'utm_term',
    resolvedFrom: 'resolved_from',
    firstTouchAt: 'first_touch_at',
    landingUrl: 'landing_url',
    // action-specific
    productId: 'product_id',
    productName: 'product_name',
    resolvedUrl: 'resolved_url',
  };

  // sessionStorage flag guarding the once-per-session landing event.
  var LANDING_FLAG = 'er_ts_landing_sent';

  /* ------------------------------------------------------------------ *
   * Transport — support gtag OR dataLayer, degrade to no-op.
   * ------------------------------------------------------------------ */
  function send(eventName, params) {
    var payload = params || {};
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
        return;
      }
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var entry = { event: eventName };
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k)) entry[k] = payload[k];
        }
        window.dataLayer.push(entry);
        return;
      }
      // No analytics loaded — nothing to do. Not an error.
    } catch (e) {
      /* never let analytics break the page */
    }
  }

  /* ------------------------------------------------------------------ *
   * Base attribution params attached to every event.
   * Guarded so a missing/broken TrafficSource still yields a valid payload.
   * ------------------------------------------------------------------ */
  function baseParams() {
    var ts = null;
    try {
      if (window.TrafficSource && typeof window.TrafficSource.get === 'function') {
        ts = window.TrafficSource.get();
      }
    } catch (e) {
      ts = null;
    }
    ts = ts || { channel: 'direct' };

    var out = {};
    out[PARAMS.channel] = ts.channel || 'direct';
    out[PARAMS.source] = ts.source || null;
    out[PARAMS.medium] = ts.medium || null;
    out[PARAMS.campaign] = ts.campaign || null;
    out[PARAMS.content] = ts.content || null;
    out[PARAMS.term] = ts.term || null;
    out[PARAMS.resolvedFrom] = ts.resolvedFrom || 'direct';
    return out;
  }

  function withBase(extra) {
    var params = baseParams();
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) params[k] = extra[k];
      }
    }
    return params;
  }

  /* ------------------------------------------------------------------ *
   * Landing event — once per session only.
   * ------------------------------------------------------------------ */
  function fireLandingOnce() {
    var already = false;
    try {
      already = window.sessionStorage.getItem(LANDING_FLAG) === '1';
    } catch (e) {
      already = false;
    }
    if (already) return;

    var ts = null;
    try {
      ts = window.TrafficSource && window.TrafficSource.get();
    } catch (e) {
      ts = null;
    }
    ts = ts || {};

    var params = withBase({});
    params[PARAMS.firstTouchAt] = ts.firstTouchAt || null;
    params[PARAMS.landingUrl] = ts.landingUrl || null;

    send(EVENTS.landing, params);

    try {
      window.sessionStorage.setItem(LANDING_FLAG, '1');
    } catch (e) {
      /* ignore — worst case the event fires again next load */
    }
  }

  /* ------------------------------------------------------------------ *
   * Delegated listeners — one handler each on document.
   * ------------------------------------------------------------------ */
  function isAddToCartForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1;
  }

  function onSubmitDelegated(evt) {
    var form = evt.target;
    if (!isAddToCartForm(form)) return;
    send(EVENTS.addToCart, withBase({}));
  }

  function closest(el, selector) {
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(selector)) return node;
      node = node.parentElement;
    }
    return null;
  }

  function onClickDelegated(evt) {
    var target = evt.target;

    // Amazon buy button — resolved data written by amazon-attribution.js.
    var amazon = closest(target, '[data-amazon-buy]');
    if (amazon) {
      var extra = {};
      extra[PARAMS.productId] = amazon.getAttribute('data-product-id') || null;
      extra[PARAMS.productName] = amazon.getAttribute('data-product-title') || null;
      extra[PARAMS.resolvedUrl] =
        amazon.getAttribute('data-amazon-resolved-url') || amazon.getAttribute('href') || null;
      // Prefer the channel the button actually resolved to (usually identical).
      var resolvedChannel = amazon.getAttribute('data-amazon-resolved-channel');
      if (resolvedChannel) extra[PARAMS.channel] = resolvedChannel;
      send(EVENTS.amazonClick, withBase(extra));
      return;
    }

    // Reaching checkout.
    var checkout = closest(target, '[name="checkout"], a[href*="/checkout"], button[href*="/checkout"]');
    if (checkout) {
      send(EVENTS.checkout, withBase({}));
    }
  }

  /* ------------------------------------------------------------------ *
   * Dawn pub/sub backup for add-to-cart (AJAX adds that don't submit a form).
   * ------------------------------------------------------------------ */
  function wirePubSub() {
    try {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
          // Only the actual add-to-cart source publishes an add; ignore other
          // cartUpdate emitters (quantity edits) when a source tag is present.
          var src = payload && payload.source;
          if (src && src !== 'product-form') return;
          send(EVENTS.addToCart, withBase({}));
        });
      }
    } catch (e) {
      /* pub/sub unavailable — delegated submit still covers the common case */
    }
  }

  /* ------------------------------------------------------------------ *
   * Init.
   * ------------------------------------------------------------------ */
  function init() {
    fireLandingOnce();
    document.addEventListener('submit', onSubmitDelegated, true);
    document.addEventListener('click', onClickDelegated, true);
    wirePubSub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed so other modules/tests can fire attributed events consistently.
  window.AnalyticsEvents = {
    EVENTS: EVENTS,
    PARAMS: PARAMS,
    track: function (eventName, extra) {
      send(eventName, withBase(extra));
    },
  };
})();
